// src/api/auth.js
import { supabase } from './supabaseClient'; // Adjust path if needed

/**
 * Fetches the currently authenticated user.
 * @returns {Promise<Object|null>} The user object or null if not authenticated
 * @throws {Error} If the request fails
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw new Error(`Failed to get user: ${error.message}`);
    return user;
  } catch (error) {
    console.error('getCurrentUser error:', error.message);
    throw error;
  }
}

/**
 * Fetches detailed user information (e.g., name, pfp) from user_metadata.
 * Also includes role information from the user_roles table.
 * @returns {Promise<Object|null>} User details or null if not authenticated
 * @throws {Error} If the request fails
 */
export async function getUserDetails() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw new Error(`Failed to get user details: ${error.message}`);
    if (!user) return null;

    // Query the user_roles table to get the user's role
    const { data: userRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .single(); // We expect only one role per user

    if (rolesError) throw new Error(`Failed to get user role: ${rolesError.message}`);

    return {
      id: user.id,
      email: user.email,
      name: user.user_metadata?.name || 'Unknown', // Fallback if name isn’t set
      pfp: user.user_metadata?.pfp || null, // Profile picture URL, null if not set
      role: userRoles?.role || 'guest', // Default to 'guest' if no role is found
    };
  } catch (error) {
    console.error('getUserDetails error:', error.message);
    throw error;
  }
}

/**
 * Signs in a user with email and password.
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} The user object and session data
 * @throws {Error} If login fails
 */
export async function signIn(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    console.log('signIn response:', { data, error }); // Log the full response
    if (error) throw new Error(`Login failed: ${error.message}`);
    return data; // Contains user and session
  } catch (error) {
    console.error('signIn error:', error.message);
    throw error;
  }
}

/**
 * Signs up a new user with email and password.
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {Object} [metadata] - Optional metadata (e.g., name, pfp)
 * @returns {Promise<Object>} The user object and session data
 * @throws {Error} If signup fails
 */
export async function signUp(email, password, metadata = {}) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata, // Store name, pfp, etc. in user_metadata
      },
    });
    if (error) throw new Error(`Signup failed: ${error.message}`);
    return data; // Contains user and session (if auto-confirmed)
  } catch (error) {
    console.error('signUp error:', error.message);
    throw error;
  }
}

/**
 * Signs out the current user.
 * @returns {Promise<void>}
 * @throws {Error} If signout fails
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(`Signout failed: ${error.message}`);
    else window.location.reload(); // Refresh the page to clear state
  } catch (error) {
    console.error('signOut error:', error.message);
    throw error;
  }
}
